import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import AnimateHeight from 'react-animate-height'

import { cn, formatPrice } from 'utils'
import TwButton, { BUTTON_VARIANTS } from 'components/Button/TwButton'
import { ArrowUp } from 'components/Icons'
import { CART_STATE, CART_STATE_REASON } from 'views/CartV2/consts'
import useOverdueInvoiceHandler from 'views/Dashboard/Invoices/Tile/OverdueTile/useOverdueInvoiceHandler'
import { ROUTES } from 'consts'

import messages from '../../messages'

const SummaryInfoBox = ({ cartData, userData, isExpandable }) => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const [isExpanded, setExpanded] = useState(!isExpandable)
  const { state, tradeLimit } = cartData
  const { value, reason } = state

  const handlePayInvoices = useOverdueInvoiceHandler()
  const handleContactCSClick = () => {
    navigate(ROUTES.PROFILE)
  }

  const infoBoxStyle = useMemo(
    () => {
      if (value === CART_STATE.BLOCKED) {
        const containerClassName =
          'bg-red-100 text-red-700 [&_path]:stroke-red-700'

        if (reason === CART_STATE_REASON.CART_LIMIT_EXCEEDED) {
          return {
            title: formatMessage(messages.cartLimitExceededTitle),
            description: formatMessage(messages.cartLimitExceededText, {
              value: formatPrice(tradeLimit.maxCartGross),
            }),
            expandBtnVariant: BUTTON_VARIANTS.alertLight,
            actionBtn: {
              variant: isExpandable
                ? BUTTON_VARIANTS.alert
                : BUTTON_VARIANTS.alertLight,
              text: formatMessage(messages.contactBOK),
              onClick: handleContactCSClick,
            },
            containerClassName,
          }
        }

        return {
          title: formatMessage(messages.accountBlockedTitle),
          description: formatMessage(
            userData.hideDues
              ? messages.accountBlocked
              : messages.accountBlockedOwner,
          ),
          actionBtn: !userData.hideDues && {
            variant: BUTTON_VARIANTS.alert,
            text: formatMessage(messages.payOverdueBills),
            onClick: handlePayInvoices,
          },
          containerClassName,
        }
      }

      if (value === CART_STATE.CONFIRMATION_REQUIRED) {
        const expandBtnVariant = BUTTON_VARIANTS.warningDark
        const containerClassName =
          'bg-orange-100 text-orange-900 [&_path]:stroke-orange-900'

        if (reason === CART_STATE_REASON.TRADE_LIMIT_USED) {
          return {
            title: formatMessage(messages.requiresApproval),
            description: formatMessage(messages.creditExceeded),
            expandBtnVariant,
            containerClassName,
          }
        }

        return {
          title: formatMessage(messages.requiresApproval),
          description: formatMessage(
            userData.hideDues
              ? messages.hasOverdueInvoices
              : messages.hasOverdueInvoicesOwner,
          ),
          expandBtnVariant,
          containerClassName,
        }
      }

      return null
    },
    [cartData, userData],
  )

  if (!infoBoxStyle) {
    return null
  }

  const {
    title,
    description,
    expandBtnVariant,
    actionBtn,
    containerClassName,
  } = infoBoxStyle

  const isExpandAvailable = isExpandable && expandBtnVariant

  return (
    <div
      data-test-id="summary-info-box"
      className={cn(
        'flex flex-col gap-4 rounded-[20px] p-5 lg:rounded-2xl lg:p-4',
        containerClassName,
      )}
    >
      <div className="flex justify-between">
        <span className="text-14 font-semibold">{title}</span>
        {isExpandAvailable && (
          <button
            className={cn('transition', !isExpanded && 'rotate-180')}
            type="button"
            onClick={() => setExpanded(!isExpanded)}
          >
            <ArrowUp />
          </button>
        )}
      </div>
      <AnimateHeight
        duration={150}
        height={!isExpandAvailable || isExpanded ? 'auto' : 0}
      >
        <span className="text-12">{description}</span>
      </AnimateHeight>
      {(actionBtn || isExpandAvailable) && (
        <div className="flex *:flex-1 gap-2">
          {isExpandAvailable && (
            <TwButton
              variant={expandBtnVariant}
              onClick={() => setExpanded(!isExpanded)}
            >
              <span className="text-label-13md">
                {formatMessage(
                  isExpanded
                    ? messages.collapseDescription
                    : messages.expandDescription,
                )}
              </span>
            </TwButton>
          )}
          {actionBtn && (
            <TwButton className={actionBtn.variant} onClick={actionBtn.onClick}>
              <span className="text-label-13md">{actionBtn.text}</span>
            </TwButton>
          )}
        </div>
      )}
    </div>
  )
}

export default SummaryInfoBox
